'use-client';
import React, { useEffect, useImperativeHandle } from 'react';
import type { IStylableButtonImperativeActions } from '../../../StylableButton/StylableButton.types';
import StylableButton from '../../../StylableButton/viewer/StylableButton';
import { useHamburgerMenuContext } from '../../HamburgerMenuContext';
/**
 * Importing semantic classes from HamburgerMenuRoot component because
 * that is the component that gets selected in the editor and
 * it is used to show available CSS classes in Studio editor
 */
import semanticClassNames from '../../HamburgerMenuRoot/HamburgerMenuRoot.semanticClassNames';
import type {
  ClassNames,
  IHamburgerOpenButtonProps,
} from '../HamburgerOpenButton.types';
import classNames from 'classnames';

const HamburgerOpenButton: React.ForwardRefRenderFunction<
  IStylableButtonImperativeActions,
  IHamburgerOpenButtonProps & { classes: ClassNames }
> = (
  { id, a11y = {}, className, classes, ariaLabel, onClick, ...props },
  ref,
) => {
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const { isMenuOpen, shouldFocus, setIsMenuOpen } = useHamburgerMenuContext();

  useImperativeHandle(ref, () => ({
    focus: () => buttonRef.current?.focus(),
    blur: () => buttonRef.current?.blur(),
  }));

  useEffect(() => {
    if (shouldFocus && isMenuOpen === false) {
      buttonRef.current?.focus();
    }
  }, [shouldFocus, isMenuOpen]);

  const onButtonClick = (event: React.MouseEvent) => {
    setIsMenuOpen(!isMenuOpen);
    onClick?.(event);
  };

  return (
    <nav aria-label={ariaLabel} className={classes.nav}>
      <StylableButton
        {...props}
        className={classNames(
          classes.hamburgerOpenButtonRoot,
          classes.stylableButtonRoot,
          className,
        )}
        a11y={{
          ...a11y,
          expanded: isMenuOpen || false,
          haspopup: 'dialog',
        }}
        id={id}
        onClick={onButtonClick}
        ref={buttonRef}
        semanticClassNames={semanticClassNames}
      />
    </nav>
  );
};

export default React.forwardRef(HamburgerOpenButton);
